import { Component, OnInit, OnDestroy, Input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

// PrimeNG imports
import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { MenuItem } from 'primeng/api';
import { FormsModule } from '@angular/forms';

// Services and Components
import { AuthService } from '../../service/auth.service';
import { environment } from 'src/environments/environment';
import { NotificationDropdownComponent } from '../notification-dropdown/notification-dropdown.component';

export interface NavbarConfig {
  moduleType: 'CAS' | 'CAC';
  moduleName: string;
  moduleTagline: string;
  logoPath?: string;
  primaryColor?: string;
}

@Component({
  selector: 'app-shared-navbar',
  standalone: true,
  imports: [
    CommonModule, 
    RouterLink, 
    MenubarModule,
    ButtonModule,
    MenuModule,
    AvatarModule,
    BadgeModule,
    TagModule,
    DropdownModule,
    FormsModule,
    NotificationDropdownComponent
  ],
  templateUrl: './shared-navbar.component.html',
  styleUrls: ['./shared-navbar.component.scss']
})
export class SharedNavbarComponent implements OnInit, OnDestroy {
  @Input() config: NavbarConfig = {
    moduleType: 'CAS',
    moduleName: 'CAS',
    moduleTagline: 'Comparateur d\'Assurance Santé'
  };

  isAuthenticated = false;
  isLoginPage = false;
  showUserMenu = false;
  items: MenuItem[] = [];
  gestionMenuItems = [];

  // Propriétés pour le sélecteur de module
  selectedModule: string = 'CAS';
  moduleOptions = [
    { label: 'CAS - Comparateur Assurance Santé', value: 'CAS' },
    { label: 'CAC - Comparateur Assurance Crédit', value: 'CAC' }
  ];
  
  private destroy$ = new Subject<void>();
  private routerSubscription: Subscription = new Subscription();
  private authSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.detectCurrentModule();
    this.initializeNavbar();
    this.setupRouterSubscription();
    this.setupAuthSubscription();
    this.checkCurrentRoute();
    
  }

  private detectCurrentModule(): void {
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/cas')) {
      this.config = {
        moduleType: 'CAS',
        moduleName: 'CAS',
        moduleTagline: 'Comparateur d\'Assurance Santé'
      };
      this.selectedModule = 'CAS';
    } else if (currentUrl.startsWith('/cac')) {
      this.config = {
        moduleType: 'CAC',
        moduleName: 'CAC',
        moduleTagline: 'Comparateur d\'Assurance Auto'
      };
      this.selectedModule = 'CAC';
    }
    
    // Écouter les changements de route pour mettre à jour le module
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      if (event.url.startsWith('/cas')) {
        this.config.moduleType = 'CAS';
        this.config.moduleName = 'CAS';
        this.selectedModule = 'CAS';
      } else if (event.url.startsWith('/cac')) {
        this.config.moduleType = 'CAC';
        this.config.moduleName = 'CAC';
        this.selectedModule = 'CAC';
      }
      this.updateMenuItems();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private initializeNavbar(): void {
    // Check initial auth state
    this.isAuthenticated = this.authService.isAuthenticated();
    this.updateMenuItems();
  }

  private setupRouterSubscription(): void {
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isLoginPage = this.isLoginRoute(event.url);
      // Re-synchroniser l'état auth sur chaque navigation (évite le besoin de F5)
      this.isAuthenticated = this.authService.isAuthenticated();
    });
  }

  private setupAuthSubscription(): void {
    this.authSubscription = this.authService.authStatus$.subscribe((isAuth: boolean) => {
      this.isAuthenticated = isAuth;
      this.updateMenuItems();
    });
  }

  private checkCurrentRoute(): void {
    this.isLoginPage = this.isLoginRoute(this.router.url);
  }

  private isLoginRoute(url: string): boolean {
    return url === '/login' || 
           url === '/auth/login' || 
           url.startsWith('/login') || 
           url.startsWith('/auth/login');
  }

  switchToModule(targetModule: 'CAS' | 'CAC'): void {
    if (targetModule === 'CAS') {
      this.router.navigate(['/cas/dashboard']);
    } else {
      this.router.navigate(['/cac/dashboard']);
    }
  }

// Mise à jour de menu items - vider complètement car navigation dans sidebar
private updateMenuItems(): void {
  // Tous les liens de navigation sont maintenant dans la sidebar
  this.items = [];
}

  // User methods
  isAdmin(): boolean {
    const role = localStorage.getItem('role');
    return role === 'ADMIN' || role === 'admin';
  }

  getUserInitials(): string {
    const username = localStorage.getItem('username') || '';
    const nameParts = username.split(' ');
    
    if (nameParts.length >= 2) {
      const firstInitial = nameParts[0].charAt(0).toUpperCase() || '';
      const lastInitial = nameParts[1].charAt(0).toUpperCase() || '';
      return `${firstInitial}${lastInitial}`;
    }
    
    return username.charAt(0).toUpperCase() || 'U';
  }

  getUserPhotoUrl(): string {
    const photoUrl = localStorage.getItem('photo_url') || '';
    if (photoUrl && photoUrl.startsWith('/api/')) {
      return `${environment.apiUrl}${photoUrl}`;
    }
    return photoUrl;
  }

  hasUserPhoto(): boolean {
    const photoUrl = localStorage.getItem('photo_url');
    return !!photoUrl;
  }

  getUserDisplayName(): string {
    return localStorage.getItem('username') || 'Utilisateur';
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  getRoleBadge(): string {
    const role = localStorage.getItem('role');
    
    switch (role) {
      case 'ADMIN':
      case 'admin':
        return 'Admin';
      case 'COMMERCIAL':
        return 'Commercial';
      case 'VIEWER':
      case 'USER':
        return 'Utilisateur';
      default:
        return '';
    }
  }

  getRoleSeverity(): string {
    const role = localStorage.getItem('role');
    
    switch (role) {
      case 'ADMIN':
      case 'admin':
        return 'danger';
      case 'COMMERCIAL':
        return 'success';
      case 'VIEWER':
      case 'USER':
        return 'info';
      default:
        return 'info';
    }
  }

  isHubSpotUser(): boolean {
    // Cette propriété n'est pas disponible dans le service existant
    return false;
  }

  get userMenuItems(): MenuItem[] {
    const username = this.getUserDisplayName();
    const email = localStorage.getItem('email');
    
    if (!username) return [];

    return [
      {
        label: username || 'Utilisateur',
        icon: 'pi pi-user',
        disabled: true,
        style: { 'font-weight': 'bold', 'opacity': 1 }
      },
      {
        label: email || 'Email non disponible',
        icon: 'pi pi-envelope',
        disabled: true,
        style: { 'font-style': 'italic', 'opacity': 1 }
      },
      { separator: true },
      {
        label: 'Profil',
        icon: 'pi pi-user',
        command: () => this.viewProfile()
      },
      {
        label: 'Paramètres',
        icon: 'pi pi-cog',
        command: () => this.router.navigate(['/settings'])
      },
      { separator: true },
      {
        label: 'Déconnexion',
        icon: 'pi pi-sign-out',
        command: () => this.logout()
      }
    ];
  }

  // Legacy getters for CAC compatibility
  get username(): string | null {
    return localStorage.getItem('username');
  }

  get email(): string | null {
    return localStorage.getItem('email');
  }

  get role(): string | null {
    return localStorage.getItem('role');
  }

  // Actions
  viewProfile(): void {
    console.log('View profile clicked');
    this.showUserMenu = false;
  }

  logout(): void {
    this.showUserMenu = false;
    this.authService.logout().subscribe({
      next: () => {
        console.log('✅ Logout successful');
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('❌ Logout error:', error);
        // Even if logout API fails, we still redirect to login
        this.authService.clearToken();
        this.router.navigate(['/login']);
      }
    });
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }


  onModuleChange(event: any): void {
    const selectedValue = event.value;
    this.switchToModule(selectedValue);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const userProfileMenu = target.closest('.user-profile-dropdown');
    
    if (!userProfileMenu && this.showUserMenu) {
      this.showUserMenu = false;
    }
  }
}
